import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Tag, Linkedin, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContentBlock } from "@/components/blog/ContentBlock";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
        setError(null);
      } catch (err) {
        setError("Blog post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Blog Post Not Found</h1>
          <Link to="/blogs">
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const contentParagraphs = blog.content.split('\n\n').filter(p => p.trim());

  return (
    <div data-testid="blog-detail-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link 
            to="/blogs" 
            className="inline-flex items-center text-slate-500 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              {blog.category}
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center text-sm text-slate-500">
              <Clock className="w-4 h-4 mr-1" />
              {blog.read_time}
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-slate-900 font-['Outfit'] mb-6">
            {blog.title}
          </h1>
          
          <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans'] mb-8">
            {blog.excerpt}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-lg font-semibold text-slate-600">
                  {blog.author.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="font-semibold text-slate-900">{blog.author}</p>
                <p className="text-sm text-slate-500">{blog.author_role}</p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              {blog.published_at}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="aspect-[16/7] overflow-hidden border border-slate-200">
            <img 
              src={blog.image_url}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg prose-slate max-w-none">
            {contentParagraphs.map((paragraph, index) => (
              <ContentBlock key={`block-${index}`} paragraph={paragraph} index={index} />
            ))}
          </article>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="h-4 w-4 text-slate-400" />
              {blog.tags.map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 bg-slate-100 text-slate-600 text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-8 flex items-center gap-4">
            <span className="text-sm text-slate-500">Share this article:</span>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-blue-400 transition-colors"
            >
              <Twitter className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Want to Learn More?
          </h2>
          <p className="text-slate-400 mb-8 font-['Plus_Jakarta_Sans']">
            Our experts are ready to help you implement these insights in your organization.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-blue-700 hover:bg-blue-800 text-white rounded-none px-8"
            >
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
